import {
  FleetTireSet,
  FleetTireSetUsage,
  FleetTireSetRow,
  FleetTireRow,
  FleetTire,
  FleetTireSetEditFormValues,
  FleetTireAddRow,
  FleetTireAddFormValues,
  FleetTireEditFormValues,
  TireType,
  TireWidth,
  TireRadius,
  TireHeight,
  FleetTireSetDocumentRow,
  FleetTireSetDocumentUploadRow,
  FleetTireSetDocumentType,
  FleetTireSetDocument,
  FleetTireSetDocumentEditFormValues,
  FleetTireSetDocumentUpload,
} from "./types";

/*---------------------------------------------------------------------------------------
    TIRE SET SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetRow = (res: any): FleetTireSetRow => {

  //ako nema aktivne lokacije seta onda mi daj zadnju koja ima
  const location =
    res.locations?.find((i: { isActive: boolean }) => i.isActive) ??
    res.locations?.[res.locations.length - 1];

  return {
    id: res.id,
    code: res.code,
    isActive: res.isActive,
    isWrittenOff: res.isWrittenOff ?? false,
    location: location,
    vehicle: res.vehicle ?? null,
    lastVehicle: res.lastVehicle ?? null,
    childRows: res.tires ? res.tires.map(transformFleetTireRow) : [],
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SET CREATE/EDIT
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSet = (res: any): FleetTireSet => {
  return {
    id: res.id,
    code: res.code,
    isActive: res.isActive,
    isWrittenOff: res.isWrittenOff ?? false,
    writtenOffAt: res.writtenOffAt ?? null,
    tires: res.tires ? res.tires.map(transformFleetTire) : [],
    warehouse: res.warehouse,
    vehicle: res.vehicle,
    totalKm: res.totalKm ?? 0,
    usages: res.usages ? res.usages.map(transformFleetTireSetUsage) : [],
    // locations: res.locations,
  };
};

export const transformFleetTireSetUsage = (res: any): FleetTireSetUsage => {
  return {
    id: res.id,
    vehicleId: res.vehicleId ?? null,
    vehicleName: res.vehicleName ?? null,
    registrationPlate: res.registrationPlate ?? null,
    mountDate: res.mountDate ? new Date(res.mountDate) : null,
    mountKm: res.mountKm ?? 0,
    demountDate: res.demountDate ? new Date(res.demountDate) : null,
    demountKm: res.demountKm ?? null,
    distanceKm: res.distanceKm ?? null,
    isOpen: res.isOpen ?? false,
    vehicleKmTraveled: res.vehicleKmTraveled ?? null,
    currentDistanceKm: res.currentDistanceKm ?? null,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SET CREATE/EDIT
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetEditForm = (res: any): FleetTireSetEditFormValues => {
  return {
    id: res.id,
    code: res.code,
    isActive: res.isActive,
    tires: res.tires ? res.tires.map(transformFleetTireAddRow) : [],
    vehicle: res.vehicle,
    warehouse: res.warehouse,
    tireSetReplacement: res.tireSetReplacement
  };
};

export const transformFleetTireSetOnCreate = (
  formValues: FleetTireSetEditFormValues,
): any => {
  return {
    isActive: formValues.isActive,
    tires: formValues.tires
      ? formValues.tires.map((tire) =>
        transformFleetTireRowOnSetCreate(tire)
      )
      : [],
    vehicleId: formValues.vehicle?.id ?? null,
    warehouseId: formValues.warehouse?.id ?? null,
  };
};

export const transformFleetTireRowOnSetCreate = (
  res: FleetTireAddRow
): FleetTireAddFormValues => {
  return {
    id: res.id,
    width: res.width,
    height: res.height,
    radius: res.radius,
    quantity: res.quantity,
    dot: res.dot,
    profileDepth: res.profileDepth,
    rof: res.rof,
    tireManufacturerId: res.tireManufacturer?.id ?? null,
    tireTypeId: res.tireType?.id ?? null,
    vehicleId: res.vehicle?.id ?? null,
    axlePosition: null,
  };
};

export const transformFleetTireSetOnUpdate = (
  formValues: FleetTireSetEditFormValues,
): any => {

  return {
    tireSetId: formValues.id,
    isActive: formValues.isActive,
    vehicleId: formValues.vehicle?.id ?? null,
    warehouseId: formValues.warehouse?.id ?? null,
    tireSetReplacement: formValues.tireSetReplacement,
    tires: formValues.tires
      ? formValues.tires.map((tire) =>
        transformFleetTireRowOnSetCreate(tire)
      )
      : [],
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SET - DOCUMENT 
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetDocument = (res: any): FleetTireSetDocument => {
  return {
    id: res.id,
    name: res.name,
    description: res.description,
    documentType: res.documentType,
    tireSet: res.tireSet,
    created: res.created,
    uploads: res.uploads,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SET - DOCUMENT - SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetDocumentRow = (
  res: any
): FleetTireSetDocumentRow => {
  return {
    id: res.id,
    name: res.name,
    description: res.description,
    documentType: res.documentType,
    vehicle: res.vehicle,
    created: res.created,
    childRows: res.uploads ? res.uploads.map((u: any) => transformFleetTireSetDocumentUploadRow(u, res)) : [],
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SET - DOCUMENT - UPLOAD
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetDocumentUploadRow = (
  res: any, parent: any
): FleetTireSetDocumentUploadRow => {
  return {
    documentId: parent.id,
    id: res.id,
    originalFileName: res.originalFileName,
    created: res.created,
    url: res.url,
  };
};


export const transformFleetTireSetDocumentUpload = (
  res: any
): FleetTireSetDocumentUpload => {

  return {
    id: res.id,
    originalFileName: res.originalFileName,
    fileExtension: res.fileExtension,
    fileSizeKb: res.fileSizeKb,
    contentType: res.contentType,
    created: res.created,
    url: res.url
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SET - DOCUMENT - CREATE
  -----------------------------------------------------------------------------------------*/
export const transformFleetTireSetDocumentOnCreate = (
  tireSetId: string,
  formValues: FleetTireSetDocumentEditFormValues,
  uploads: FleetTireSetDocumentUpload[]
): any => ({
  documentTypeId: formValues.documentTypeId,
  tireSetId: tireSetId,
  name: formValues.name,
  description: formValues.description,
  uploadIds: uploads.map((u) => u.id),
});

/*---------------------------------------------------------------------------------------
    TIRE SET - DOCUMENT - UPDATE
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetDocumentOnUpdate = (
  tireSetId: string,
  formValues: FleetTireSetDocumentEditFormValues,
  uploads: FleetTireSetDocumentUpload[]
): any => ({
  documentTypeId: formValues.documentTypeId,
  tireSetId: tireSetId,
  name: formValues.name,
  description: formValues.description,
  uploadIds: uploads.map((u) => u.id),
});

/*---------------------------------------------------------------------------------------
    TIRE SET - DOCUMENT - TYPE
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireSetDocumentType = (
  res: any
): FleetTireSetDocumentType => {
  return {
    id: res.id,
    name: res.name,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE SEARCH
  -----------------------------------------------------------------------------------------*/
export const transformFleetTireRow = (res: any): FleetTireRow => {

  return {
    id: res.id,
    name: res.name,
    width: res.width,
    height: res.height,
    radius: res.radius,
    tireType: res.tireType,
    tireManufacturer: res.tireManufacturer,
    dot: res.dot,
    profileDepth: res.profileDepth,
  };
};

export const transformFleetTire = (res: any): FleetTire => {
  return {
    id: res.id,
    name: res.name,
    width: res.width,
    height: res.height,
    radius: res.radius,
    tireType: res.tireType,
    tireManufacturer: res.tireManufacturer,
    dot: res.dot,
    profileDepth: res.profileDepth,
  };
};

export const transformFleetTireAddRow = (res: any): FleetTireAddRow => {
  return {
    id: res.id,
    width: res.width,
    height: res.height,
    radius: res.radius,
    dot: res.dot,
    profileDepth: res.profileDepth,
    rof: res.rof,
    tireManufacturer: res.tireManufacturer,
    tireType: res.tireType,
    warehouse: res.warehouse,
    vehicle: res.vehicle,
    quantity: res.quantity,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE  CREATE/EDIT
  -----------------------------------------------------------------------------------------*/

export const transformFleetTireOnCreate = (
  formValues: FleetTireAddFormValues
): any => {
  return {
    width: formValues.width,
    height: formValues.height,
    radius: formValues.radius,
    quantity: formValues.quantity,
    tireTypeId: formValues.tireTypeId,
    dot: formValues.dot,
    profileDepth: formValues.profileDepth,
    rof: formValues.rof,
    tireManufacturerId: formValues.tireManufacturerId,
    vehicleId: formValues.vehicleId,
    axlePosition: formValues.axlePosition,
  };
};

export const transformFleetTireOnUpdate = (
  formValues: FleetTireEditFormValues
): any => {
  return {
    id: formValues.id,
    width: formValues.width,
    height: formValues.height,
    radius: formValues.radius,
    tireTypeId: formValues.tireTypeId,
    dot: formValues.dot,
    profileDepth: formValues.profileDepth,
    rof: formValues.rof,
    tireManufacturerId: formValues.tireManufacturerId,
    axlePosition: formValues.axlePosition,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE TYPE
  -----------------------------------------------------------------------------------------*/
export const transformFleetTireTypeRow = (res: any): TireType => {
  return {
    id: res.id,
    name: res.name,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE WIDTH
  -----------------------------------------------------------------------------------------*/
export const transformFleetTireWidthRow = (res: any): TireWidth => {

  return {
    id: res,
    name: res,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE HEIGHT
  -----------------------------------------------------------------------------------------*/
export const transformFleetTireHeightRow = (res: any): TireHeight => {
  return {
    id: res,
    name: res,
  };
};

/*---------------------------------------------------------------------------------------
    TIRE radius
  -----------------------------------------------------------------------------------------*/
export const transformFleetTireradiusRow = (res: any): TireRadius => {
  return {
    id: res,
    name: res,
  };
};






